import {
  Component,
  ElementRef,
  Input,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ModalComponent } from '../../../../../shared/components/modal/modal.component';
import { ClipboardService } from '../../../../../core/services/clipboard/clipboard.service';

@Component({
  selector: 'app-realtime-json-modal',
  standalone: true,
  imports: [CommonModule, ModalComponent],
  templateUrl: './realtime-json-modal.component.html',
  styleUrl: './realtime-json-modal.component.scss',
})
export class RealtimeJsonModalComponent {
  @ViewChild(ModalComponent) private readonly modal!: ModalComponent;

  private readonly _data = signal<unknown | null>(null);
  private readonly sanitizer = inject(DomSanitizer);
  private readonly clipboard = inject(ClipboardService);

  readonly copied = signal(false);

  @Input() set data(value: unknown | null) {
    this._data.set(value);
  }

  readonly raw = computed(() => {
    const value = this._data();
    if (value == null) return '';
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  });

  readonly html = computed<SafeHtml>(() => {
    const json = this.raw();
    if (!json) return this.sanitizer.bypassSecurityTrustHtml('');
    return this.sanitizer.bypassSecurityTrustHtml(highlightJson(json));
  });

  open(): void {
    this.modal?.openDialog();
  }

  close(): void {
    this.modal?.closeDialog();
  }

  copy(): void {
    const text = this.raw();
    if (!text) return;
    this.clipboard.copy(text);
    this.copied.set(true);
    setTimeout(() => this.copied.set(false), 1500);
  }
}

function highlightJson(json: string): string {
  const escaped = json
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  return escaped.replace(
    /("(?:\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(\s*:)?|\b(?:true|false|null)\b|-?\d+(?:\.\d*)?(?:[eE][+\-]?\d+)?)/g,
    (match) => {
      let cls = 'pm-num';
      if (/^"/.test(match)) {
        cls = /:$/.test(match) ? 'pm-key' : 'pm-str';
      } else if (/true|false/.test(match)) {
        cls = 'pm-bool';
      } else if (/null/.test(match)) {
        cls = 'pm-null';
      }
      return `<span class="${cls}">${match}</span>`;
    }
  );
}
